import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { Routes, Route, Navigate, NavLink, useNavigate, useLocation } from "react-router-dom";
import "./Layout.css";
import { connect } from "react-redux";
import * as actions from "../../store/actions";

import { styled } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";

import Assignment from "@mui/icons-material/AssignmentOutlined";
import Speed from "@mui/icons-material/Speed";
import Layers from "@mui/icons-material/DnsOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import Add from "@mui/icons-material/Add";
import ErrorIcon from "@mui/icons-material/Error";
import LogoutIcon from "@mui/icons-material/Logout";

import Dashboard from "../Dashboard/Dashboard";
import Orders from "../Orders/Orders";
import Inventory from "../Inventory/Inventory";
import Profile from "../Profile/Profile";

import AddProductPage from "../AddItem/AddProductPage.jsx";
import Help from "../Help/Help";
import OrderDetails from "../OrderDetails/OrderDetails";
import AggregatedOrderDetails from "../AggregatedOrderDetails/AggregatedOrderDetails";

import axios from "../../services/apiClient";
import ItemDetails from "../ItemDetails/ItemDetails";
import { toast } from "react-toastify";
import Spinner from "../../containers/UI/Spinner/Spinner";

const drawerWidth = 240;

// Styled Components
const RootContainer = styled(Box)({
  display: "flex",
});

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  background: "white",
  color: "black",
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  background: "#2E3C54",
  color: "#8CA2C8",
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : theme.spacing(7) + 1,
    background: "#2E3C54",
    color: "#8CA2C8",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: open 
        ? theme.transitions.duration.enteringScreen 
        : theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    [theme.breakpoints.up("sm")]: {
      width: open ? drawerWidth : theme.spacing(7) + 1,
    },
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  background: "#475772",
  ...theme.mixins.toolbar,
}));

const ToolbarContentSpacer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const ToolbarFlex = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  marginLeft: "16px",
});

const ToolbarRight = styled(Box)({
  display: "flex",
});

const MainContent = styled('main')({
  flexGrow: 1,
});

const StyledListItem = styled(ListItem)({
  background: "inherit !important",
});

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  color: active ? "#FF6271" : "#8CA2C8",
}));

const SearchContainer = styled(Box)({
  display: "flex",
  width: "700px",
  '@media (max-width: 1450px)': {
    width: "500px",
  },
});

const SearchInput = styled('input')({
  background: "#FFFFFF 0% 0% no-repeat padding-box",
  border: "1px solid #DCDEE3",
  borderRadius: "4px 0 0 4px",
  height: "40px",
  width: "100%",
  paddingLeft: "10px",
  fontSize: "17px",
  '&::placeholder': {
    color: "#cdcdcd",
  },
});

const SearchIconButton = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "40px",
  width: "48px",
  borderRadius: "0px 4px 4px 0px",
  background: "transparent linear-gradient(90deg, #FEA80E 0%, #FF6271 100%) 0% 0% no-repeat padding-box",
  color: "white",
  cursor: "pointer",
});

const AddButton = styled(Box)({
  width: "130px",
  height: "40px",
  background: "transparent linear-gradient(90deg, #FEA80E 0%, #FF6271 100%) 0% 0% no-repeat padding-box",
  borderRadius: "4px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "white",
  fontSize: "14px",
  cursor: "pointer",
});

const NotificationButton = styled(Box)({
  width: "169px",
  height: "40px",
  border: "1px solid #FEA80E",
  borderRadius: "4px",
  marginRight: "15px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "#FEA80E",
  fontSize: "14px",
  cursor: "pointer",
});

const StyledList = styled(List)({
  paddingTop: "33px",
});

const LogoutButton = styled(Box)({
  position: "absolute",
  bottom: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "200px",
  height: "45px",
  background: "transparent linear-gradient(90deg, #FEA80E 0%, #FF6271 100%) 0% 0% no-repeat padding-box",
  borderRadius: "4px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "white",
  fontSize: "16px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "opacity 0.3s ease",
  "&:hover": {
    opacity: 0.85,
  },
});

const Layout = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [open, setOpen] = useState(true);
  const [profileBoxOpen, setProfileBoxOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState("dashboard");
  const [notificationNumber, setNotificationNumber] = useState(0);
  const [placeholderText, setPlaceholderText] = useState("Search");
  const hasProfileLoadedRef = useRef(false);

  const getNotificationNumber = async () => {
    // Guard: skip if profile not loaded
    if (!props.profileDetails) {
      return;
    }

    const merchantId = props.profileDetails.id;
    
    if (!merchantId) {
      return;
    }

    try {
      // Use new backend API to get pending orders count
      const response = await axios.get('/orders/pending');
      
      if (response.data && response.data.data) {
        const pendingOrders = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data.orders || [];
        
        // Filter by merchantId to get only this merchant's pending orders
        const merchantPendingOrders = pendingOrders.filter(
          order => order.merchantId === merchantId
        );
        
        setNotificationNumber(merchantPendingOrders.length);
      }
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      // Don't show error to user, just log it
      setNotificationNumber(0);
    }
  };

  // Track when profile has been loaded successfully (to prevent full-page spinner)
  useEffect(() => {
    if (props.profileDetails && !hasProfileLoadedRef.current) {
      hasProfileLoadedRef.current = true;
    }
  }, [props.profileDetails]);

  useEffect(() => {
    let locationName = window.location.pathname;
    let str = locationName.substring(locationName.lastIndexOf("/") + 1);
    setSelectedRoute(str);

    // call for notification number
    getNotificationNumber();
    
    // Set up notification timer
    const notifTimer = setInterval(() => {
      getNotificationNumber();
    }, 60000);

    // Cleanup on unmount
    return () => {
      clearInterval(notifTimer);
    };
  }, []);

  const handleNotificationClick = () => {
    routeSelectHandler("orders", "Orders search");
    navigate(`/orders?statusID=${1}`);
  };

  const handleProfileBoxOpen = () => {
    setProfileBoxOpen(!profileBoxOpen);
  };

  const closeProfileBox = () => {
    setProfileBoxOpen(false);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const routeSelectHandler = (value, placeholder) => {
    setSelectedRoute(value);
    setPlaceholderText(placeholder);
    props.deleteSearch();
  };

  const logoutHandler = () => {
    closeProfileBox();
    props.logout();
  };

  const onSearchHandler = async (e) => {
    await props.setSearchInput(e.target.value);

    let locationName = window.location.pathname;
    let str = locationName.substring(locationName.lastIndexOf("/") + 1);

    const {
      searchInput,
      perPageSelectOrder,
      sortDateOrderOrder,
      selectedPageOrder,
      perPageSelectInventory,
      sortAZInventory,
      selectedPageInventory,
      profileDetails,
      category,
      statusID,
      orderStatusID,
    } = props;

    switch (str) {
      case "orders":
        props.getOrder(
          perPageSelectOrder,
          selectedPageOrder,
          sortDateOrderOrder,
          searchInput,
          profileDetails.id,
          orderStatusID
        );
        break;

      case "inventory":
        props.getInventory(
          perPageSelectInventory,
          selectedPageInventory,
          sortAZInventory,
          searchInput,
          profileDetails.id,
          category,
          statusID
        );
        break;

      default:
        break;
    }

    str = parseInt(str);

    if (!isNaN(str) && typeof str === "number") {
      props.getInventory(
        perPageSelectInventory,
        selectedPageInventory,
        sortAZInventory,
        searchInput,
        profileDetails.id,
        category,
        statusID
      );
    }
  };

  const onKeyDownHandler = (e) => {
    if (e.keyCode === 13) {
      onGoBackHandler();
    }
  };

  const onGoBackHandler = () => {
    let locationName = window.location.pathname;
    let str = locationName.substring(locationName.lastIndexOf("/") + 1);
    str = parseInt(str);
    if (!isNaN(str) && typeof str === "number") {
      navigate(-1);
    }
  };

  const onProfileSettingsClick = () => {
    routeSelectHandler("profile", "Profile");
    navigate("/profile");
  };

  const onAddItemClick = () => {
    navigate('/add-product');
  };

  const handlerLogoClick = () => {
    navigate("/dashboard");
    routeSelectHandler("dashboard", "Dashboard search");
  };

  const { searchInput, profileDetails } = props;

  // Guard clause: only show spinner on initial load
  if (!profileDetails && !hasProfileLoadedRef.current) {
    return <Spinner />;
  }

  return (
    <RootContainer>
      <CssBaseline />

      <StyledAppBar position="fixed" open={open}>
        <Toolbar>
          <ToolbarFlex>
            <SearchContainer id="searchBar">
              {selectedRoute === "inventory" || selectedRoute === "orders" ? (
                <>
                  <SearchInput
                    type="text"
                    placeholder={placeholderText}
                    value={searchInput}
                    onChange={onSearchHandler}
                    onKeyDown={onKeyDownHandler}
                  />
                  <SearchIconButton onClick={onGoBackHandler}>
                    <SearchIcon />
                  </SearchIconButton>
                </>
              ) : null}
            </SearchContainer>
            <ToolbarRight>
              {notificationNumber ? (
                <NotificationButton onClick={handleNotificationClick}>
                  <ErrorIcon
                    color="inherit"
                    fontSize="small"
                    style={{ marginRight: "5px" }}
                  />
                  {notificationNumber} Pending orders
                </NotificationButton>
              ) : null}
              <AddButton onClick={onAddItemClick}>
                <Add color="inherit" fontSize="small" />
                Add product
              </AddButton>
            </ToolbarRight>
          </ToolbarFlex>
        </Toolbar>
      </StyledAppBar>
      <StyledDrawer variant="permanent" open={open}>
        <DrawerHeader>
          <div className="headerLogo" onClick={handlerLogoClick}>
            <img
              src={`${process.env.PUBLIC_URL}/img/DButler-Web-Logo-DarkBackground.png`}
              alt="Logo"
            />
          </div>
        </DrawerHeader>
        <div className="drawerProfil" onClick={onProfileSettingsClick}>
          <div className="drawerProfileImage">
            <img
              src={profileDetails?.logoUrl || `${process.env.PUBLIC_URL}/img/default-merchant-logo.svg`}
              alt="Merchant Logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `${process.env.PUBLIC_URL}/img/default-merchant-logo.svg`;
              }}
            />
          </div>
          <div className="drawerProfileData">
            <span>{profileDetails?.name}</span>
            <span>Profile Settings</span>
          </div>
        </div>

        <StyledList>
          <NavLink to="dashboard" className="nav-link-item">
            <StyledListItem
              key={"Dashboard"}
              onClick={() =>
                routeSelectHandler("dashboard", "Dashboard search")
              }
            >
              <StyledListItemIcon active={selectedRoute === "dashboard"}>
                <Speed color="error" />
              </StyledListItemIcon>
              <ListItemText primary="Dashboard" />
            </StyledListItem>
          </NavLink>
          <NavLink to="orders" className="nav-link-item">
            <StyledListItem
              key={"My Orders"}
              onClick={() =>
                routeSelectHandler("orders", "Orders search")
              }
            >
              <StyledListItemIcon active={selectedRoute === "orders"}>
                <Assignment color="error" />
              </StyledListItemIcon>
              <ListItemText primary="My Orders" />
            </StyledListItem>
          </NavLink>
          <NavLink to="inventory" className="nav-link-item">
            <StyledListItem
              key={"Inventory"}
              onClick={() =>
                routeSelectHandler("inventory", "Inventory search")
              }
            >
              <StyledListItemIcon active={selectedRoute === "inventory"}>
                <Layers color="error" />
              </StyledListItemIcon>
              <ListItemText primary="Inventory" />
            </StyledListItem>
          </NavLink>
          <NavLink to="help/help-topics" className="nav-link-item">
            <StyledListItem
              key={"Help"}
              onClick={() => routeSelectHandler("help", "Help search")}
            >
              <StyledListItemIcon active={selectedRoute === "help"}>
                <HelpOutlineIcon color="error" />
              </StyledListItemIcon>
              <ListItemText primary="Help" />
            </StyledListItem>
          </NavLink>
        </StyledList>
        <LogoutButton onClick={logoutHandler}>
          <LogoutIcon style={{ marginRight: "8px" }} />
          Logout
        </LogoutButton>
      </StyledDrawer>
      <MainContent>
        <ToolbarContentSpacer />
        <Routes>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="orders" element={<Orders />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="add-product" element={<AddProductPage />} />
          <Route path="itemdetails/:productMerchantId" element={<ItemDetails />} />
          <Route path="profile" element={<Profile />} />
          <Route path="help/*" element={<Help />} />
          <Route path="order-details/:orderID" element={<OrderDetails />} />
          <Route path="aggregated-order-details/:aggOrderId" element={<AggregatedOrderDetails />} />
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="*" element={<h1>404 Page not found</h1>} />
        </Routes>
      </MainContent>
    </RootContainer>
  );
};

const mapStateToProps = (state) => {
  return {
    profileDetails: state.profile.profileDetails,
    searchInput: state.search.searchInput,
    perPageSelectOrder: state.orders.perPageSelect,
    sortDateOrderOrder: state.orders.sortDateOrder,
    selectedPageOrder: state.orders.selectedPage,
    perPageSelectInventory: state.orders.perPageSelect,
    sortAZInventory: state.inventory.sortAZ,
    selectedPageInventory: state.orders.selectedPage,
    category: state.inventory.category,
    statusID: state.inventory.statusID,
    orderStatusID: state.orders.statusID,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    setSearchInput: (str) => dispatch(actions.updateSearch(str)),
    getOrder: (
      perPageSelect,
      selectedPage,
      dateOrder,
      searchInput,
      supplierID,
      statusID
    ) =>
      dispatch(
        actions.getOrders(
          perPageSelect,
          selectedPage,
          dateOrder,
          searchInput,
          supplierID,
          statusID
        )
      ),
    getInventory: (
      perPageSelect,
      selectedPage,
      sortBy,
      searchInput,
      supplierID,
      category,
      statusID
    ) =>
      dispatch(
        actions.getInventory(
          perPageSelect,
          selectedPage,
          sortBy,
          searchInput,
          supplierID,
          category,
          statusID
        )
      ),
    deleteSearch: () => dispatch(actions.deleteSearch()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
